import type { CommissionImportReport, CommissionsView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Checkbox, EmptyState, ErrorState, Input, LoadingState, Select, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";

export function CommissionsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [carrier, setCarrier] = useState(config.agency.carriers[0]?.name ?? "");
  const [statementDate, setStatementDate] = useState(new Date().toISOString().slice(0, 10));
  const [csv, setCsv] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [report, setReport] = useState<CommissionImportReport | null>(null);

  const history = useQuery({ queryKey: ["commissions"], queryFn: async () => (await callAction<"agency.commission.list", CommissionsView>("agency.commission.list", {})).result });
  const imp = useMutation({
    mutationFn: () => callAction<"agency.commission.import", CommissionImportReport>("agency.commission.import", { carrier, statementDate, csv, dryRun }),
    onSuccess: (r) => {
      setReport(r.result);
      if (!r.result.dryRun) {
        toast({ title: t("commissions.imported"), tone: "success" });
        qc.invalidateQueries({ queryKey: ["commissions"] });
        qc.invalidateQueries({ queryKey: ["report"] });
      }
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const statusTone = (s: string) => (s === "matched" ? "green" : s === "duplicate" ? "amber" : "red");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{t("commissions.title")}</h1>
        <p className="text-ink/60">{t("commissions.subtitle")}</p>
      </div>

      <Card>
        <CardHeader title={t("commissions.import")} />
        <div className="px-5 pb-5 space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Select label={t("commissions.carrier")} value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              {config.agency.carriers.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </Select>
            <Input type="date" label={t("commissions.statementDate")} value={statementDate} onChange={(e) => setStatementDate(e.target.value)} />
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-ink/80">{t("commissions.file")}</span>
              <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0]?.text().then(setCsv)} className="block w-full text-sm file:mr-3 file:min-h-[40px] file:rounded-xl file:border-0 file:bg-ink file:px-3 file:text-white file:font-semibold" />
            </label>
          </div>
          <Textarea label={t("commissions.paste")} value={csv} onChange={(e) => setCsv(e.target.value)} className="font-mono text-xs min-h-[120px]" placeholder={"Policy Number,Insured,Transaction,Effective Date,Premium,Commission,Rate\nPRG1234567,Maria Garcia,Renewal,10/01/2026,842.00,101.04,12"} />
          <div className="flex flex-wrap items-center gap-3">
            <Checkbox label={t("commissions.dryRun")} checked={dryRun} onChange={setDryRun} />
            <Button onClick={() => imp.mutate()} disabled={!csv.trim() || !carrier} loading={imp.isPending}><Upload className="h-4 w-4" />{t("commissions.import")}</Button>
          </div>

          {report ? (
            <div className="rounded-2xl border border-ink/10 bg-surface p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge tone={report.dryRun ? "blue" : "green"}>{report.dryRun ? t("commissions.dryRun") : t("common.done")}</Badge>
                <Badge tone="green">{t("commissions.matched")}: {report.matched}</Badge>
                <Badge tone="red">{t("commissions.unmatched")}: {report.unmatched}</Badge>
                <Badge tone="amber">{t("commissions.duplicates")}: {report.duplicates}</Badge>
                <Badge tone="ink">{t("commissions.total")}: {money(report.totalCommissionCents)}</Badge>
              </div>
              {report.warnings.length ? (
                <div className="text-sm">
                  <p className="font-semibold">{t("commissions.warnings")}</p>
                  <ul className="list-disc pl-5">{report.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              ) : null}
              <LinesTable lines={report.lines} />
            </div>
          ) : null}
        </div>
      </Card>

      <section className="space-y-3">
        <h2 className="text-lg font-bold">{t("commissions.history")}</h2>
        {history.isLoading ? <LoadingState /> : null}
        {history.isError ? <ErrorState error={history.error} onRetry={() => history.refetch()} /> : null}
        {history.data && history.data.statements.length === 0 ? <EmptyState title={t("commissions.none")} /> : null}
        {history.data && history.data.statements.length > 0 ? (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink/55">
                <tr>
                  <th className="px-4 py-3">{t("commissions.carrier")}</th>
                  <th className="px-4 py-3">{t("commissions.statementDate")}</th>
                  <th className="px-4 py-3 text-right">{t("commissions.lines")}</th>
                  <th className="px-4 py-3 text-right">{t("commissions.matched")}</th>
                  <th className="px-4 py-3 text-right">{t("commissions.total")}</th>
                </tr>
              </thead>
              <tbody>
                {history.data.statements.map((s) => (
                  <tr key={s.id} className="border-t border-ink/5">
                    <td className="px-4 py-2 font-semibold">{s.carrier}</td>
                    <td className="px-4 py-2">{shortDate(s.statementDate, locale)}</td>
                    <td className="px-4 py-2 text-right">{s.lineCount}</td>
                    <td className="px-4 py-2 text-right"><span className={cn(s.matchedCount < s.lineCount && "text-amber-800 font-semibold")}>{s.matchedCount}/{s.lineCount}</span></td>
                    <td className="px-4 py-2 text-right">{money(s.totalCommissionCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : null}
        {history.data && history.data.lines.length > 0 ? (
          <Card className="p-4"><LinesTable lines={history.data.lines} /></Card>
        ) : null}
      </section>
    </div>
  );

  function LinesTable({ lines }: { lines: CommissionImportReport["lines"] }) {
    if (!lines.length) return null;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink/55">
            <tr>
              <th className="px-3 py-2">{t("commissions.policyNumber")}</th>
              <th className="px-3 py-2">{t("commissions.insured")}</th>
              <th className="px-3 py-2 hidden sm:table-cell">{t("common.kind")}</th>
              <th className="px-3 py-2 text-right hidden md:table-cell">{t("common.premium")}</th>
              <th className="px-3 py-2 text-right">{t("commissions.commission")}</th>
              <th className="px-3 py-2 text-right hidden md:table-cell">{t("commissions.rate")}</th>
              <th className="px-3 py-2">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-ink/5">
                <td className="px-3 py-2 font-mono text-xs">{l.policyNumber}</td>
                <td className="px-3 py-2">{l.customerId ? <Link className="hover:underline font-semibold" to={`/customers/${l.customerId}`}>{l.insuredName}</Link> : l.insuredName}</td>
                <td className="px-3 py-2 hidden sm:table-cell">{t(`commissions.kind.${l.transactionKind}`)}</td>
                <td className="px-3 py-2 text-right hidden md:table-cell">{money(l.premiumCents)}</td>
                <td className="px-3 py-2 text-right">{money(l.commissionCents)}</td>
                <td className="px-3 py-2 text-right hidden md:table-cell">{l.ratePct == null ? "—" : `${l.ratePct}%`}</td>
                <td className="px-3 py-2"><Badge tone={statusTone(l.status)}>{t(`commissions.status.${l.status}`)}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}
